import { Tabs } from "antd"
import { useLocation, useNavigate } from "react-router-dom"

import { ROLE_IDS } from "../../../../constants/roles.ts"
import { RoutesPaths } from "../../../../constants/routes.ts"
import { useAppSelector } from "../../../../hooks/use-app-selector.ts"
import { type PermissionSettingsPermissions } from "../../../SettingsPermissions/types/permissions.types.ts"

type TabItem = {
  key: string
  label: string
  isVisible?: (
    permissions: PermissionSettingsPermissions
  ) => boolean | undefined
}

// A tab stays active on its nested pages, so the longest matching key wins.
const resolveActiveKey = (items: TabItem[], pathname: string) =>
  items
    .filter(({ key }) => pathname === key || pathname?.startsWith(`${key}/`))
    .reduce((longest, item) => {
      return item.key.length > longest.length ? item.key : longest
    }, "")

interface SettingsLayoutTabsProps {
  permissions: PermissionSettingsPermissions
}

export default function SettingsLayoutTabs({
  permissions
}: SettingsLayoutTabsProps) {
  const navigate = useNavigate()
  const pathname = useLocation()?.pathname
  const roleId = useAppSelector((state) => state.auth.userInfo?.role?.id)

  const canManageConfiguration =
    roleId === ROLE_IDS.ADMIN_GCC || roleId === ROLE_IDS.ADMIN_PLATFORM

  const allTabs: TabItem[] = [
    {
      key: RoutesPaths.settings.profile,
      label: "Profile & User"
    },
    {
      key: RoutesPaths.settings.company,
      label: "Company & System",
      isVisible: (p) => p?.company?.settings_company_view
    },
    {
      key: RoutesPaths.settings.templates,
      label: "Templates",
      isVisible: (p) =>
        p?.project_templates?.settings_template_view ||
        p?.document_templates?.settings_proposal_template_view ||
        p?.permissions?.settings_permissions_view
    },
    {
      key: RoutesPaths.settings.permissions,
      label: "Permissions",
      isVisible: (p) => p?.permissions?.settings_permissions_view
    },
    {
      key: RoutesPaths.settings.notifications,
      label: "Notifications"
    },
    {
      key: RoutesPaths.settings.configuration,
      label: "Configurations",
      isVisible: () => canManageConfiguration
    }
  ]

  const items = allTabs?.filter(
    ({ isVisible }) => !isVisible || !!isVisible(permissions)
  )

  const activeKey = resolveActiveKey(items, pathname)

  return (
    <div className="border-b border-gray-200">
      <Tabs
        activeKey={activeKey || items?.[0]?.key}
        items={items}
        onChange={navigate}
        className="[&_.ant-tabs-nav]:mb-0 [&_.ant-tabs-tab]:py-1"
      />
    </div>
  )
}
